/**
 * @fileoverview Retour du guichet de paiement.
 *
 * Simulation frontend du polling : on affiche d'abord une vérification, puis
 * un état PAID avec enregistrement encore en cours côté STI (la quittance
 * officielle relève du compte Quipux, hors GNPC — décision client 16/07).
 */

import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Button, StatusBadge, PaymentProofActions, PortalState } from '@gnpc/ui';
import { History } from 'lucide-react';

type PaymentReturnStatus = 'paid' | 'pending' | 'expired' | 'failed' | 'error';

function getPaymentReturnStatus(value: string | null): PaymentReturnStatus {
  if (value === 'pending' || value === 'expired' || value === 'failed' || value === 'error') {
    return value;
  }
  return 'paid';
}

interface PaymentReturnShellProps {
  eyebrow: string;
  title: string;
  children: ReactNode;
}

function PaymentReturnShell({ eyebrow, title, children }: PaymentReturnShellProps): JSX.Element {
  return (
    <div className="mx-auto grid max-w-xl gap-5">
      <header className="space-y-2">
        <p className="text-quipux-orange-600 text-sm font-medium">{eyebrow}</p>
        <h1 className="text-quipux-navy-950 text-3xl font-semibold tracking-tight">{title}</h1>
      </header>
      {children}
    </div>
  );
}

export function PaymentReturnPage(): JSX.Element {
  const [params] = useSearchParams();
  const txn = params.get('txn');
  const returnStatus = getPaymentReturnStatus(params.get('status'));
  const [resolved, setResolved] = useState(false);

  useEffect(() => {
    const timeout = window.setTimeout(() => setResolved(true), 1400);
    return () => window.clearTimeout(timeout);
  }, []);

  if (!txn) {
    return (
      <div className="mx-auto max-w-xl">
        <PortalState
          status="error"
          // Ce cas ne passe pas par `PaymentReturnShell` (qui fournit le
          // `<h1>`) : le titre du panneau porte donc le `<h1>` de la page
          // (axe `page-has-heading-one`).
          titleAs="h1"
          title="Transaction introuvable"
          action={
            <Button asChild variant="outline">
              <Link to="/recherche">Retour à la recherche</Link>
            </Button>
          }
        >
          Le lien de retour ne contient pas d'identifiant de transaction.
        </PortalState>
      </div>
    );
  }

  if (!resolved) {
    return (
      <PaymentReturnShell eyebrow="Retour du guichet" title="Vérification du paiement">
        <PortalState
          status="pending"
          title="Vérification de votre paiement"
          progressValue={58}
          badge={<StatusBadge kind="payment" status="PENDING" />}
        >
          Nous confirmons le retour du guichet de paiement. Gardez cette page ouverte quelques
          instants.
        </PortalState>
      </PaymentReturnShell>
    );
  }

  if (returnStatus === 'pending') {
    return (
      <PaymentReturnShell eyebrow="Paiement en cours" title="Confirmation en attente">
        <PortalState
          status="pending"
          title="Paiement en cours de confirmation"
          progressValue={72}
          badge={<StatusBadge kind="payment" status="PENDING" />}
          action={
            <Button asChild variant="outline">
              <Link to="/historique">Voir mon historique</Link>
            </Button>
          }
        >
          Le paiement n'est pas encore confirmé par le guichet. Le statut sera visible dans votre
          historique dès qu'il évolue.
        </PortalState>
      </PaymentReturnShell>
    );
  }

  if (returnStatus === 'expired') {
    return (
      <PaymentReturnShell eyebrow="Session expirée" title="Reprendre le paiement">
        <PortalState
          status="expired"
          title="La session de paiement a expiré"
          badge={<StatusBadge kind="payment" status="EXPIRED" />}
          action={
            <Button asChild>
              <Link to="/paiement">Relancer le paiement</Link>
            </Button>
          }
          secondaryAction={
            <Button asChild variant="ghost">
              <Link to="/recherche">Modifier la sélection</Link>
            </Button>
          }
        >
          Aucun paiement n'a été confirmé pour cette session. Relancez le paiement depuis votre
          sélection.
        </PortalState>
      </PaymentReturnShell>
    );
  }

  if (returnStatus === 'failed') {
    return (
      <PaymentReturnShell eyebrow="Paiement refusé" title="Le règlement n'a pas abouti">
        <PortalState
          status="failed"
          title="Paiement refusé"
          badge={<StatusBadge kind="payment" status="FAILED" />}
          action={
            <Button asChild>
              <Link to="/paiement">Réessayer le paiement</Link>
            </Button>
          }
          secondaryAction={
            <Button asChild variant="ghost">
              <Link to="/recherche">Retour à la sélection</Link>
            </Button>
          }
        >
          Aucun règlement n'a été confirmé. Vous pouvez relancer le guichet ou vérifier votre moyen
          de paiement.
        </PortalState>
      </PaymentReturnShell>
    );
  }

  if (returnStatus === 'error') {
    return (
      <PaymentReturnShell eyebrow="Retour du guichet" title="Vérification indisponible">
        <PortalState
          status="error"
          title="Impossible de confirmer le paiement"
          action={
            <Button type="button" onClick={() => window.location.reload()}>
              Réessayer
            </Button>
          }
          secondaryAction={
            <Button asChild variant="ghost">
              <Link to="/historique">Voir mon historique</Link>
            </Button>
          }
        >
          Le portail ne peut pas vérifier le statut maintenant. Si le débit a eu lieu, le
          justificatif apparaîtra dans votre historique.
        </PortalState>
      </PaymentReturnShell>
    );
  }

  return (
    <PaymentReturnShell eyebrow="Paiement confirmé" title="Votre paiement a bien été effectué">
      <PortalState
        status="success"
        title="Paiement enregistré"
        badge={<StatusBadge kind="payment" status="PAID" />}
      >
        Votre justificatif de débit est disponible maintenant. La quittance officielle sera à
        télécharger depuis votre compte Quipux après l'enregistrement chez Quipux.
      </PortalState>

      <section className="border-border bg-card rounded-lg border p-4">
        <div className="flex items-center justify-between gap-3">
          <span className="text-muted-foreground text-sm">Statut paiement</span>
          <StatusBadge kind="payment" status="PAID" />
        </div>
        <div className="mt-3 flex items-center justify-between gap-3">
          <span className="text-muted-foreground text-sm">Enregistrement Quipux</span>
          <StatusBadge kind="sgi" status="PENDING" />
        </div>
      </section>

      <PaymentProofActions debitNoteAvailable />

      <Button asChild variant="ghost">
        <Link to="/historique">
          <History />
          Voir mon historique
        </Link>
      </Button>
    </PaymentReturnShell>
  );
}
